/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../theme/ThemeContext';
import EventsTab from '../components/applications/EventsTab';
import EventTriggersTab from '../components/applications/EventTriggersTab';
import RecordHooksTab from '../components/applications/RecordHooksTab';

export default function EventHandlingPage() {
    const { theme } = useTheme();
    const { id: applicationId } = useParams();
    const navigate = useNavigate();

    const [activeTab, setActiveTab] = useState('events');

    return (
        <div className="d-flex flex-column w-100" style={{ background: '#f8fafc', minHeight: '100vh', overflowX: 'hidden' }}>
            {/* Header */}
            <div className="w-100 px-3 py-3 px-md-4 py-md-4 bg-white border-bottom d-flex flex-wrap align-items-center justify-content-between gap-3 shadow-sm" style={{ zIndex: 10 }}>
                <div className="d-flex align-items-center gap-3">
                    <button
                        className="btn btn-light rounded-circle shadow-sm"
                        onClick={() => navigate(`/dashboard/applications/${applicationId}`)}
                        style={{ width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                        <i className="bi bi-arrow-left"></i>
                    </button>
                    <div>
                        <h4 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                            <i className="bi bi-lightning-charge-fill text-primary"></i>
                            Event Framework
                        </h4>
                        <small className="text-muted d-none d-md-block">
                            Configure application events and automated triggers.
                        </small>
                    </div>
                </div>
            </div>

            <div className="container-fluid p-3 p-md-4" style={{ maxWidth: 1400 }}>
                {/* Tabs Navigation */}
                <div className="nav nav-pills mb-4 bg-white p-2 rounded shadow-sm border d-inline-flex" role="tablist">
                    <button
                        className={`nav-link fw-semibold px-4 py-2 ${activeTab === 'events' ? 'active shadow-sm' : 'text-muted'}`}
                        onClick={() => setActiveTab('events')}
                        style={{
                            borderRadius: theme.buttonRadius || '6px',
                            backgroundColor: activeTab === 'events' ? theme.primary : 'transparent',
                            color: activeTab === 'events' ? '#fff' : 'inherit',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        <i className="bi bi-calendar-event me-2"></i> Events
                    </button>
                    <button
                        className={`nav-link fw-semibold px-4 py-2 ms-2 ${activeTab === 'triggers' ? 'active shadow-sm' : 'text-muted'}`}
                        onClick={() => setActiveTab('triggers')}
                        style={{
                            borderRadius: theme.buttonRadius || '6px',
                            backgroundColor: activeTab === 'triggers' ? theme.primary : 'transparent',
                            color: activeTab === 'triggers' ? '#fff' : 'inherit',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        <i className="bi bi-diagram-3 me-2"></i> Event Triggers
                    </button>
                    <button
                        className={`nav-link fw-semibold px-4 py-2 ms-2 ${activeTab === 'hooks' ? 'active shadow-sm' : 'text-muted'}`}
                        onClick={() => setActiveTab('hooks')}
                        style={{
                            borderRadius: theme.buttonRadius || '6px',
                            backgroundColor: activeTab === 'hooks' ? theme.primary : 'transparent',
                            color: activeTab === 'hooks' ? '#fff' : 'inherit',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        <i className="bi bi-braces me-2"></i> Hooks
                    </button>
                </div>

                {/* Tab Content */}
                <div className="tab-content w-100">
                    {activeTab === 'events'   && <EventsTab applicationId={applicationId} navigate={navigate} />}
                    {activeTab === 'triggers' && <EventTriggersTab applicationId={applicationId} navigate={navigate} />}
                    {activeTab === 'hooks'    && <RecordHooksTab applicationId={applicationId} />}
                </div>
            </div>
        </div>
    );
}
